// La Loge vue de World (section « Loges ») : appels aux fonctions serveur /api/loge/studio/* avec le jeton de session
// Supabase du compte, comme Google Agenda et les rendez-vous. Contrat : docs/LOGE-API.md.
// Les réponses sont lues avec indulgence (camelCase attendu, snake_case accepté) : un champ absent ne casse rien.
import { CHUNK_BYTES, initials, visibleFolders, type LogeLang, type ProposalStatus } from '../core/loge';
import { chunkRanges } from '../core/logePreview';
import { cleanXrMeta, xrFrameCount, type XrMeta } from '../core/logeXr';

// ── Types ─────────────────────────────────────────────────────────────────────────────────────────

export interface StepView { n: number; fr: string; en: string; state: 'done' | 'now' | 'todo'; plan_fr: string; plan_en: string }

export interface StudioMember {
  id: string; clientId: string; email: string; name: string; role: string;
  /** null : tous les projets de la maison. */
  projectIds: string[] | null;
  lang: LogeLang | null; createdAt: string | null; revokedAt: string | null; invitedAt: string | null; lastSeenAt: string | null; initials: string;
}

export interface StudioProjectSummary {
  id: string; clientId: string; number: number; name: string; kind: string; template: string;
  status: 'active' | 'completed' | 'archived'; currentStep: number; steps: StepView[];
  downloadsUnlocked: boolean; pending: number; files: number; updatedAt: string | null; cover: string | null;
}

export interface StudioClient {
  id: string; name: string; lang: LogeLang; downloadsUnlocked: boolean; createdAt: string | null;
  /** Maison fermée : ses personnes ne peuvent plus se connecter. Rien n'est perdu, cela se rouvre. */
  archivedAt: string | null;
  members: StudioMember[]; projects: StudioProjectSummary[];
}

/** Ce que les clients ont fait : note, demande d'appel, envoi, validation ; « other » pour une ligne inconnue. */
export type InboxKind = 'note' | 'call' | 'upload' | 'approve' | 'other';

export interface InboxItem {
  id: string; kind: InboxKind; createdAt: string;
  clientId: string; clientName: string; projectId: string; projectName: string;
  proposalId: string | null; proposalTitle: string | null; round: number | null;
  fileId: string | null; fileName: string | null; thumb: string | null;
  memberName: string; body: string;
  /** Phrase toute faite du serveur (« Claire Martin a laissé une note sur Rendus 3D »). */
  text: string;
}

/** Proposition ouverte, en attente du client. */
export interface WaitingItem { proposalId: string; projectId: string; projectName: string; clientId: string; clientName: string; title: string; round: number; step: number; createdAt: string }

export interface StudioOverview { clients: StudioClient[]; inbox: InboxItem[]; waiting: WaitingItem[] }

/** `cover` : dossier caché de la couverture du projet, jamais montré dans l'arbre. */
export interface LogeFolder { id: string; step: number | null; parentId: string | null; name: string; kind: 'studio' | 'client' | 'cover'; position: number; deletedAt: string | null }

export interface LogeFile {
  id: string; folderId: string; name: string; tag: string;
  family: 'image' | 'pdf' | 'video' | '3d' | 'archive' | 'other';
  version: number; latest: boolean; size: number; caption: string; createdAt: string;
  uploadedBy: 'studio' | 'client'; memberName: string | null; previews: string[];
  /** Maquette 3D de consultation (lien signé du studio), ou null. */
  model: string | null;
  /** Vidéo : lien de lecture signé (2 h), ou null. C'est le fichier d'origine, lu en place. */
  stream: string | null;
  /** Rendu 360° (KeyShotXR) : grille des vues ; les pages d'aperçu sont les vues (vue de départ en page 0). */
  xr: XrMeta | null;
  /** Exception posée par le studio (« Autoriser le téléchargement dès maintenant »). */
  allowDownload: boolean;
  /** Téléchargeable par le client en ce moment (exception, envoi du client, ou projet débloqué). */
  downloadable: boolean;
  deletedAt: string | null; readyAt: string | null;
  /** Envoi commencé mais jamais clos (`readyAt` explicitement vide) : à ne pas montrer. */
  incomplete: boolean;
}

export interface LogeNote { author: 'client' | 'studio'; name: string; body: string; createdAt: string }

export interface LogeProposal {
  id: string; step: number; folderId: string | null; title: string; caption: string; round: number;
  status: ProposalStatus; createdAt: string; decidedAt: string | null; fileIds: string[]; notes: LogeNote[];
  reportId: string | null; advancesStep: boolean;
}

export interface LogeDecision { id: string; proposalId: string; memberName: string; action: 'approve' | 'note' | 'call'; note: string; createdAt: string }
/** `url` : lien signé ouvrable sans en-tête (vue du studio) ; sinon lecture avec le jeton. */
export interface LogeReport { id: string; title: string; createdAt: string; url: string | null }
export interface LogeActivity { actor: string; name: string; kind: string; text: string; createdAt: string }
export interface TeamMember { name: string; role: string; initials: string; studio: boolean }

export interface StudioProject {
  project: {
    id: string; clientId: string; number: number; name: string; kind: string; status: string;
    currentStep: number; steps: StepView[];
    /** Débloqué pour ce projet ou pour toute la maison. */
    downloadsUnlocked: boolean;
    projectUnlocked: boolean; clientUnlocked: boolean;
    coverFileId: string | null;
  };
  client: { id: string; name: string; lang: LogeLang; downloadsUnlocked: boolean } | null;
  folders: LogeFolder[];
  files: LogeFile[];
  proposals: LogeProposal[];
  decisions: LogeDecision[];
  reports: LogeReport[];
  activity: LogeActivity[];
  team: TeamMember[];
}

// ── Lecture indulgente ────────────────────────────────────────────────────────────────────────────

type Raw = Record<string, unknown>;
const obj = (v: unknown): Raw => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Raw) : {});
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
function pick(o: Raw, ...keys: string[]): unknown {
  for (const k of keys) if (o[k] !== undefined && o[k] !== null) return o[k];
  return undefined;
}
const str = (o: Raw, ...keys: string[]): string => { const v = pick(o, ...keys); return typeof v === 'string' ? v : typeof v === 'number' ? String(v) : ''; };
const strOrNull = (o: Raw, ...keys: string[]): string | null => { const v = pick(o, ...keys); return typeof v === 'string' && v ? v : null; };
const num = (o: Raw, fallback: number, ...keys: string[]): number => { const v = Number(pick(o, ...keys)); return Number.isFinite(v) ? v : fallback; };
const bool = (o: Raw, ...keys: string[]): boolean => pick(o, ...keys) === true;
const lang = (v: unknown): LogeLang => (v === 'en' ? 'en' : 'fr');

function readSteps(v: unknown, current: number): StepView[] {
  return arr(v).map((s, i) => {
    const o = obj(s);
    const n = num(o, i + 1, 'n');
    const state = o.state === 'done' || o.state === 'now' || o.state === 'todo' ? o.state : n < current ? 'done' : n === current ? 'now' : 'todo';
    return { n, fr: str(o, 'fr'), en: str(o, 'en'), state, plan_fr: str(o, 'plan_fr', 'planFr'), plan_en: str(o, 'plan_en', 'planEn') };
  });
}

function readMember(v: unknown, clientId = ''): StudioMember {
  const o = obj(v);
  const name = str(o, 'name');
  const email = str(o, 'email');
  const ids = pick(o, 'projectIds', 'project_ids');
  const l = pick(o, 'lang');
  return {
    id: str(o, 'id'), clientId: str(o, 'clientId', 'client_id') || clientId, email, name, role: str(o, 'role'),
    projectIds: Array.isArray(ids) ? ids.map(String) : null,
    lang: l === 'fr' || l === 'en' ? l : null,
    createdAt: strOrNull(o, 'createdAt', 'created_at'), revokedAt: strOrNull(o, 'revokedAt', 'revoked_at'), invitedAt: strOrNull(o, 'invitedAt', 'invited_at'),
    lastSeenAt: strOrNull(o, 'lastSeenAt', 'last_seen_at'),
    initials: str(o, 'initials') || initials(name || email || '?'),
  };
}

function readProjectSummary(v: unknown, clientId = ''): StudioProjectSummary {
  const o = obj(v);
  const current = num(o, 1, 'currentStep', 'current_step');
  const status = o.status === 'completed' || o.status === 'archived' ? o.status : 'active';
  return {
    id: str(o, 'id'), clientId: str(o, 'clientId', 'client_id') || clientId, number: num(o, 1, 'number'), name: str(o, 'name'),
    kind: str(o, 'kind'), template: str(o, 'template') || 'montre', status, currentStep: current, steps: readSteps(o.steps, current),
    downloadsUnlocked: bool(o, 'downloadsUnlocked', 'downloads_unlocked'),
    pending: num(o, 0, 'pending', 'openProposals', 'open_proposals'), files: num(o, 0, 'files', 'fileCount', 'file_count'),
    updatedAt: strOrNull(o, 'updatedAt', 'updated_at'), cover: strOrNull(o, 'cover'),
  };
}

function readInbox(v: unknown, clients: StudioClient[]): InboxItem {
  const o = obj(v);
  const rawKind = str(o, 'kind', 'type');
  const kind: InboxKind = rawKind === 'call' || rawKind === 'note' || rawKind === 'approve' ? rawKind : rawKind === 'client_upload' || rawKind === 'upload' ? 'upload' : 'other';
  const clientId = str(o, 'clientId', 'client_id');
  const projectId = str(o, 'projectId', 'project_id');
  const client = clients.find((c) => c.id === clientId) ?? clients.find((c) => c.projects.some((p) => p.id === projectId));
  const project = client?.projects.find((p) => p.id === projectId);
  const round = pick(o, 'round');
  return {
    id: str(o, 'id') || `${kind}-${str(o, 'createdAt', 'created_at')}`, kind, createdAt: str(o, 'createdAt', 'created_at'),
    clientId: clientId || client?.id || '', clientName: str(o, 'clientName', 'client_name') || client?.name || '',
    projectId, projectName: str(o, 'projectName', 'project_name') || project?.name || '',
    proposalId: strOrNull(o, 'proposalId', 'proposal_id'), proposalTitle: strOrNull(o, 'proposalTitle', 'proposal_title', 'title'),
    round: typeof round === 'number' ? round : null,
    fileId: strOrNull(o, 'fileId', 'file_id'), fileName: strOrNull(o, 'fileName', 'file_name', 'name'), thumb: strOrNull(o, 'thumb'),
    memberName: str(o, 'memberName', 'member_name', 'author'), body: str(o, 'body', 'note'),
    text: str(o, 'text_fr', 'textFr', 'text'),
  };
}

export function readOverview(data: unknown): StudioOverview {
  const d = obj(data);
  const looseMembers = arr(d.members).map((m) => readMember(m));
  const looseProjects = arr(d.projects).map((p) => readProjectSummary(p));
  const clients: StudioClient[] = arr(d.clients).map((c) => {
    const o = obj(c);
    const id = str(o, 'id');
    const members = Array.isArray(o.members) ? o.members.map((m) => readMember(m, id)) : looseMembers.filter((m) => m.clientId === id);
    const projects = Array.isArray(o.projects) ? o.projects.map((p) => readProjectSummary(p, id)) : looseProjects.filter((p) => p.clientId === id);
    projects.sort((a, b) => a.number - b.number);
    return {
      id, name: str(o, 'name'), lang: lang(o.lang), downloadsUnlocked: bool(o, 'downloadsUnlocked', 'downloads_unlocked'),
      createdAt: strOrNull(o, 'createdAt', 'created_at'), archivedAt: strOrNull(o, 'archivedAt', 'archived_at'), members, projects,
    };
  });
  const inbox = arr(d.inbox).map((i) => readInbox(i, clients)).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const waiting = arr(d.waiting).map((w) => {
    const o = obj(w);
    return {
      proposalId: str(o, 'proposalId', 'proposal_id'), projectId: str(o, 'projectId', 'project_id'), projectName: str(o, 'projectName', 'project_name'),
      clientId: str(o, 'clientId', 'client_id'), clientName: str(o, 'clientName', 'client_name'), title: str(o, 'title'),
      round: num(o, 1, 'round'), step: num(o, 1, 'step'), createdAt: str(o, 'createdAt', 'created_at'),
    };
  }).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  return { clients, inbox, waiting };
}

function readFile(v: unknown): LogeFile {
  const o = obj(v);
  const fam = str(o, 'family');
  const family = (['image', 'pdf', 'video', '3d', 'archive', 'other'] as const).find((f) => f === fam) ?? 'other';
  const allow = pick(o, 'exception', 'allowDownload', 'allow_download');
  const downloadable = bool(o, 'downloadable');
  const previews = arr(o.previews).filter((p): p is string => typeof p === 'string');
  const xr = cleanXrMeta(o.xr);
  return {
    id: str(o, 'id'), folderId: str(o, 'folderId', 'folder_id'), name: str(o, 'name'), tag: str(o, 'tag') || 'FICHIER', family,
    version: num(o, 1, 'version'), latest: pick(o, 'latest') !== false, size: num(o, 0, 'size'), caption: str(o, 'caption'),
    createdAt: str(o, 'createdAt', 'created_at'), uploadedBy: o.uploadedBy === 'client' || o.uploaded_by === 'client' ? 'client' : 'studio',
    memberName: strOrNull(o, 'memberName', 'member_name'),
    previews,
    model: strOrNull(o, 'model'),
    stream: strOrNull(o, 'stream'),
    xr: xr && previews.length === xrFrameCount(xr) ? xr : null,
    allowDownload: typeof allow === 'boolean' ? allow : downloadable,
    downloadable,
    deletedAt: strOrNull(o, 'deletedAt', 'deleted_at'), readyAt: strOrNull(o, 'readyAt', 'ready_at'),
    incomplete: o.ready === false || (('readyAt' in o || 'ready_at' in o) && !strOrNull(o, 'readyAt', 'ready_at')),
  };
}

function readProposal(v: unknown): LogeProposal {
  const o = obj(v);
  const st = str(o, 'status');
  const status: ProposalStatus = st === 'approved' || st === 'discussing' || st === 'replaced' || st === 'withdrawn' ? st : 'open';
  return {
    id: str(o, 'id'), step: num(o, 1, 'step'), folderId: strOrNull(o, 'folderId', 'folder_id'), title: str(o, 'title'), caption: str(o, 'caption'),
    round: num(o, 1, 'round'), status, createdAt: str(o, 'createdAt', 'created_at'), decidedAt: strOrNull(o, 'decidedAt', 'decided_at'),
    fileIds: arr(pick(o, 'fileIds', 'file_ids')).map(String),
    notes: arr(o.notes).map((n) => { const x = obj(n); return { author: x.author === 'studio' ? 'studio' : 'client', name: str(x, 'name'), body: str(x, 'body'), createdAt: str(x, 'createdAt', 'created_at') } as LogeNote; }),
    reportId: strOrNull(o, 'reportId', 'report_id'), advancesStep: pick(o, 'advancesStep', 'advances_step') !== false,
  };
}

export function readProject(data: unknown): StudioProject {
  const d = obj(data);
  const p = obj(d.project);
  const current = num(p, 1, 'currentStep', 'current_step');
  const c = obj(d.client);
  const files = arr(d.files).map(readFile);
  const folders = visibleFolders(arr(d.folders).map((f) => {
    const o = obj(f);
    const step = pick(o, 'step');
    return {
      id: str(o, 'id'), step: typeof step === 'number' ? step : null, parentId: strOrNull(o, 'parentId', 'parent_id'),
      name: str(o, 'name'), kind: o.kind === 'client' ? 'client' : o.kind === 'cover' ? 'cover' : 'studio',
      position: num(o, 0, 'position'), deletedAt: strOrNull(o, 'deletedAt', 'deleted_at'),
    } as LogeFolder;
  }).sort((a, b) => a.position - b.position || a.name.localeCompare(b.name, 'fr')), files);
  return {
    project: {
      id: str(p, 'id'), clientId: str(p, 'clientId', 'client_id') || str(c, 'id'), number: num(p, 1, 'number'), name: str(p, 'name'), kind: str(p, 'kind'),
      status: str(p, 'status') || 'active', currentStep: current, steps: readSteps(p.steps, current),
      downloadsUnlocked: bool(p, 'downloadsUnlocked', 'downloads_unlocked') || bool(c, 'downloadsUnlocked', 'downloads_unlocked'),
      projectUnlocked: typeof p.projectUnlocked === 'boolean' ? p.projectUnlocked : bool(p, 'downloadsUnlocked', 'downloads_unlocked'),
      clientUnlocked: typeof p.clientUnlocked === 'boolean' ? p.clientUnlocked : bool(c, 'downloadsUnlocked', 'downloads_unlocked'),
      coverFileId: strOrNull(p, 'coverFileId', 'cover_file_id'),
    },
    client: d.client ? { id: str(c, 'id'), name: str(c, 'name'), lang: lang(c.lang), downloadsUnlocked: bool(c, 'downloadsUnlocked', 'downloads_unlocked') } : null,
    folders,
    files,
    proposals: arr(d.proposals).map(readProposal).sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    decisions: arr(d.decisions).map((x) => { const o = obj(x); const a = str(o, 'action'); return { id: str(o, 'id'), proposalId: str(o, 'proposalId', 'proposal_id'), memberName: str(o, 'memberName', 'member_name', 'name'), action: a === 'approve' || a === 'call' ? a : 'note', note: str(o, 'note'), createdAt: str(o, 'createdAt', 'created_at') } as LogeDecision; }),
    reports: arr(d.reports).map((x) => { const o = obj(x); return { id: str(o, 'id'), title: str(o, 'title'), createdAt: str(o, 'createdAt', 'created_at'), url: strOrNull(o, 'url') }; }),
    activity: arr(d.activity).map((x) => { const o = obj(x); return { actor: str(o, 'actor'), name: str(o, 'name'), kind: str(o, 'kind'), text: str(o, 'text_fr', 'textFr', 'text'), createdAt: str(o, 'createdAt', 'created_at') }; }),
    team: arr(d.team).map((x) => { const o = obj(x); const name = str(o, 'name'); return { name, role: str(o, 'role'), initials: str(o, 'initials') || initials(name || '?'), studio: bool(o, 'studio') }; }),
  };
}

// ── Appels ────────────────────────────────────────────────────────────────────────────────────────

/** Bilan des e-mails d'une action : partis, en échec. */
export interface MailReport { mailSent: boolean; mailed: number; failed: number }
function mailReport(r: Raw): MailReport {
  const mailed = num(r, r.mailSent === true ? 1 : 0, 'mailed');
  const failed = num(r, 0, 'failed');
  return { mailSent: r.mailSent === true, mailed, failed };
}

export class LogeError extends Error {
  constructor(message: string, public readonly code: string, public readonly status: number) {
    super(message);
    this.name = 'LogeError';
  }
}

let tokenProvider: () => Promise<string | null> = async () => null;

/** Fournit le jeton de session Supabase aux appels serveur. */
export function setLogeTokenProvider(fn: () => Promise<string | null>): void {
  tokenProvider = fn;
}

async function authHeader(): Promise<Record<string, string>> {
  const token = await tokenProvider();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Message en français pour un code d'erreur du contrat. */
export function errorMessage(code: string, status: number): string {
  switch (code) {
    case 'unauthorized': return 'Session expirée : reconnectez-vous à World.';
    case 'forbidden': return 'Ce compte n’a pas accès aux Loges.';
    case 'not_found': return 'Introuvable : l’élément a peut-être été retiré. Rechargez la page.';
    case 'too_large': return 'Fichier trop lourd pour La Loge.';
    case 'type': case 'unsupported': return 'Format de fichier refusé.';
    case 'rate_limited': return 'Trop de demandes d’un coup : patientez une minute.';
    case 'not_configured': return 'La Loge n’est pas encore configurée côté serveur (stockage R2 ou base).';
    case 'closed': return 'Cette décision est déjà close.';
    case 'network': return 'Le serveur n’a pas répondu (réseau).';
  }
  if (status === 401) return 'Session expirée : reconnectez-vous à World.';
  if (status === 403) return 'Accès refusé.';
  if (status === 404) return 'Les fonctions de La Loge ne répondent pas ici (404).';
  if (status === 413) return 'Fichier trop lourd pour La Loge.';
  if (status === 429) return 'Trop de demandes d’un coup : patientez une minute.';
  if (status >= 500) return `Le serveur de La Loge est en erreur (HTTP ${status}).`;
  return `Erreur du serveur (HTTP ${status}).`;
}

async function call<T = Raw>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      cache: 'no-store',
      ...init,
      headers: { ...(init.headers as Record<string, string> | undefined), ...(await authHeader()), ...(typeof init.body === 'string' ? { 'content-type': 'application/json' } : {}) },
    });
  } catch {
    throw new LogeError(errorMessage('network', 0), 'network', 0);
  }
  let data: Raw;
  try { data = (await res.json()) as Raw; }
  catch { throw new LogeError(res.ok ? 'Réponse illisible du serveur.' : errorMessage('', res.status), 'server', res.status); }
  if (!res.ok || typeof data.error === 'string') {
    const code = typeof data.error === 'string' ? data.error : 'server';
    throw new LogeError(typeof data.message === 'string' && data.message ? data.message : errorMessage(code, res.status), code, res.status);
  }
  return data as T;
}

const post = <T = Raw>(path: string, body: unknown) => call<T>(path, { method: 'POST', body: JSON.stringify(body) });
const q = encodeURIComponent;

export const logeStudio = {
  overview: async (): Promise<StudioOverview> => readOverview(await call('/api/loge/studio/overview')),
  project: async (id: string): Promise<StudioProject> => readProject(await call(`/api/loge/studio/project?id=${q(id)}`)),
  createClient: async (input: { name: string; lang: LogeLang }): Promise<{ id: string }> => {
    const r = await post('/api/loge/studio/client', input);
    return { id: str(obj(r.client), 'id') || str(r, 'id', 'clientId') };
  },
  /** Ajoute une personne, ou met à jour celle qui a déjà cette adresse dans la maison. */
  saveMember: async (input: { clientId: string; email: string; name: string; role: string; projectIds: string[] | null; lang?: LogeLang | null }): Promise<{ id: string }> => {
    const { lang: l, ...rest } = input;
    const r = await post('/api/loge/studio/member', { ...rest, ...(l ? { lang: l } : {}) });
    return { id: str(obj(r.member), 'id') || str(r, 'id', 'memberId') };
  },
  revokeMember: (memberId: string) => post('/api/loge/studio/member/revoke', { memberId }),
  /** `url` : le lien d'invitation, renvoyé seulement quand l'e-mail n'a pas pu partir. */
  invite: async (memberId: string): Promise<{ mailSent: boolean; url: string }> => {
    const r = await post('/api/loge/studio/invite', { memberId });
    return { mailSent: r.mailSent !== false, url: str(r, 'url') };
  },
  createProject: async (input: { clientId: string; name: string; kind: string; template: string }): Promise<{ id: string }> => {
    const r = await post('/api/loge/studio/project', input);
    return { id: str(obj(r.project), 'id') || str(r, 'id', 'projectId') };
  },
  createFolder: async (input: { projectId: string; step: number | null; parentId: string | null; name: string }): Promise<{ id: string }> => {
    const r = await post('/api/loge/studio/folder', input);
    return { id: str(obj(r.folder), 'id') || str(r, 'id', 'folderId') };
  },
  /** `withdrawn` : demandes retirées d'elles-mêmes, faute de fichier hors de la corbeille. */
  updateFile: async (input: { fileId: string; caption?: string; downloadable?: boolean; deleted?: boolean }): Promise<{ withdrawn: Array<{ id: string; title: string }> }> => {
    const r = await post('/api/loge/studio/file/update', input);
    return { withdrawn: arr(r.withdrawn).map((x) => { const o = obj(x); return { id: str(o, 'id'), title: str(o, 'title') }; }) };
  },
  /** Retire une demande encore à décider : elle disparaît chez le client. */
  withdraw: (proposalId: string) => post('/api/loge/studio/proposal/withdraw', { proposalId }),
  /** Étape en cours, posée à la main (rouvrir une étape validée, ou avancer sans validation). */
  setStep: (projectId: string, currentStep: number) => post('/api/loge/studio/project', { id: projectId, currentStep }),
  /** Archiver un projet le retire de La Loge du client sans rien perdre ; `active` le rouvre. */
  setProjectStatus: (projectId: string, status: 'active' | 'completed' | 'archived') => post('/api/loge/studio/project', { id: projectId, status }),
  /** Fermer une maison : ses personnes ne peuvent plus se connecter. `false` la rouvre. */
  archiveClient: (clientId: string, archived: boolean) => post('/api/loge/studio/client', { id: clientId, archived }),
  /** Sans retour : la ligne, ses dossiers, ses fichiers (jusque dans le stockage), ses décisions, ses comptes rendus. */
  deleteProject: async (projectId: string, confirm: string): Promise<{ files: number; storageError: string | null }> => {
    const r = await post('/api/loge/studio/project/delete', { id: projectId, confirm });
    return { files: num(r, 0, 'files'), storageError: strOrNull(r, 'storageError') };
  },
  /** Sans retour : la maison, ses personnes et tous ses projets. */
  deleteClient: async (clientId: string, confirm: string): Promise<{ projects: number; files: number; storageError: string | null }> => {
    const r = await post('/api/loge/studio/client/delete', { id: clientId, confirm });
    return { projects: num(r, 0, 'projects'), files: num(r, 0, 'files'), storageError: strOrNull(r, 'storageError') };
  },
  propose: async (input: { projectId: string; folderId: string; step: number; title: string; caption: string; fileIds: string[]; advancesStep: boolean; notify: boolean }): Promise<{ id: string; round: number } & MailReport> => {
    const r = await post('/api/loge/studio/proposal', input);
    const p = obj(r.proposal);
    return { id: str(p, 'id') || str(r, 'id', 'proposalId'), round: num(p, num(r, 1, 'round'), 'round'), ...mailReport(r) };
  },
  reply: async (proposalId: string, body: string): Promise<MailReport> => mailReport(await post('/api/loge/studio/reply', { proposalId, body })),
  /** Débloque les téléchargements d'un projet ou d'une maison ; `unlocked: false` les referme. */
  unlock: (target: { projectId: string } | { clientId: string }, unlocked = true) => post('/api/loge/studio/unlock', { ...target, unlocked }),

  /** Compte rendu (page HTML imprimable), lu avec le jeton du studio. */
  reportHtml: async (id: string): Promise<string> => {
    let res: Response;
    try { res = await fetch(`/api/loge/report?id=${q(id)}`, { cache: 'no-store', headers: await authHeader() }); }
    catch { throw new LogeError(errorMessage('network', 0), 'network', 0); }
    if (!res.ok) {
      let code = 'server';
      try { const d = (await res.json()) as Raw; if (typeof d.error === 'string') code = d.error; } catch { /* page d'erreur */ }
      throw new LogeError(errorMessage(code, res.status), code, res.status);
    }
    return res.text();
  },

  /** Original d'un fichier (envoi d'un client), lu avec le jeton du studio. */
  download: async (fileId: string): Promise<Blob> => {
    let res: Response;
    try { res = await fetch(`/api/loge/download?f=${q(fileId)}`, { cache: 'no-store', headers: await authHeader() }); }
    catch { throw new LogeError(errorMessage('network', 0), 'network', 0); }
    if (!res.ok) {
      let code = 'server';
      try { const d = (await res.json()) as Raw; if (typeof d.error === 'string') code = d.error; } catch { /* corps vide */ }
      throw new LogeError(code === 'locked' ? 'Téléchargement refusé par le serveur.' : errorMessage(code, res.status), code, res.status);
    }
    return res.blob();
  },
};

// ── Envoi d'un fichier : start → chunk × n → preview × p → model → finish ─────────────────────────────

export interface UploadInput {
  folderId: string;
  /** Couverture du projet : envoyée dans son dossier caché, `folderId` est alors ignoré. */
  cover?: { projectId: string };
  file: File;
  caption?: string;
  downloadable?: boolean;
  previews: Blob[];
  /** Maquette 3D de consultation (GLB), envoyée après les aperçus. */
  model?: Blob | null;
  /** Rendu 360° (export KeyShotXR) : les aperçus sont ses vues, dans l'ordre des pages. */
  xr?: XrMeta | null;
}

/** État d'un envoi, gardé entre deux essais : un nouvel essai reprend là où le précédent s'est arrêté. */
export interface UploadProgress {
  fileId: string | null;
  uploadId: string | null;
  version: number | null;
  chunkBytes: number;
  parts: Array<{ part: number; etag: string }>;
  previewsSent: number;
  /** Maquette 3D envoyée, ou raison de son échec (le fichier part quand même, sans elle). */
  modelSent: boolean;
  modelError: string | null;
  finished: boolean;
}

export function newUploadProgress(): UploadProgress {
  return { fileId: null, uploadId: null, version: null, chunkBytes: CHUNK_BYTES, parts: [], previewsSent: 0, modelSent: false, modelError: null, finished: false };
}

/** Erreurs qui ne passeront pas en réessayant : inutile d'insister. */
const PERMANENT = new Set([400, 401, 403, 404, 409, 413, 415]);
export const UPLOAD_ATTEMPTS = 3;
/** Aperçus envoyés en même temps : un rendu 360° en compte des dizaines, voire des centaines. */
export const PREVIEW_PARALLEL = 4;

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => { clearTimeout(t); reject(new DOMException('Envoi annulé.', 'AbortError')); }, { once: true });
  });
}

/** PUT d'un corps binaire avec suivi de progression (XMLHttpRequest : fetch ne donne pas l'avancement de l'envoi). */
function putBinary(url: string, body: Blob, contentType: string, onBytes: (loaded: number) => void, signal?: AbortSignal): Promise<Raw> {
  return authHeader().then((headers) => new Promise<Raw>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    xhr.setRequestHeader('content-type', contentType);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onBytes(e.loaded); };
    xhr.onload = () => {
      let data: Raw = {};
      try { data = JSON.parse(xhr.responseText || '{}') as Raw; } catch { /* corps vide */ }
      if (xhr.status >= 200 && xhr.status < 300 && typeof data.error !== 'string') { onBytes(body.size); resolve(data); return; }
      const code = typeof data.error === 'string' ? data.error : 'server';
      reject(new LogeError(typeof data.message === 'string' && data.message ? data.message : errorMessage(code, xhr.status), code, xhr.status));
    };
    xhr.onerror = () => reject(new LogeError(errorMessage('network', 0), 'network', 0));
    xhr.ontimeout = () => reject(new LogeError('Le serveur a mis trop de temps à répondre.', 'timeout', 0));
    const abort = () => xhr.abort();
    xhr.onabort = () => reject(new DOMException('Envoi annulé.', 'AbortError'));
    signal?.addEventListener('abort', abort, { once: true });
    xhr.onloadend = () => signal?.removeEventListener('abort', abort);
    xhr.send(body);
  }));
}

/** Réessaie une requête qui a échoué pour une raison passagère (réseau, 5xx, 429), avec une attente croissante. */
async function withRetry<T>(what: string, fn: () => Promise<T>, signal?: AbortSignal): Promise<T> {
  let last: unknown;
  for (let attempt = 1; attempt <= UPLOAD_ATTEMPTS; attempt++) {
    try { return await fn(); }
    catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') throw e;
      last = e;
      if (e instanceof LogeError && PERMANENT.has(e.status)) break;
      if (attempt < UPLOAD_ATTEMPTS) await sleep(1200 * attempt * attempt, signal);
    }
  }
  const reason = last instanceof Error ? last.message : String(last);
  const permanent = last instanceof LogeError && PERMANENT.has(last.status);
  throw new LogeError(permanent ? `${what} : ${reason}` : `${what} n’a pas abouti après ${UPLOAD_ATTEMPTS} essais. ${reason}`, last instanceof LogeError ? last.code : 'server', last instanceof LogeError ? last.status : 0);
}

/**
 * Envoie un fichier dans La Loge. L'original part en morceaux (≤ CHUNK_BYTES), puis les aperçus, la maquette 3D s'il y
 * en a une, puis la clôture (`finish`) : le fichier n'existe pour le client qu'après cette dernière étape, jamais à
 * moitié envoyé.
 * `state` est complété au fil de l'envoi ; en cas d'échec, rappeler la fonction avec le même `state` reprend.
 */
export async function uploadToLoge(input: UploadInput, state: UploadProgress, onProgress: (sent: number, total: number) => void, signal?: AbortSignal): Promise<UploadProgress> {
  const { file, previews } = input;
  const model = input.model ?? null;
  const previewBytes = previews.reduce((s, b) => s + b.size, 0);
  const total = file.size + previewBytes + (model?.size ?? 0);

  if (!state.fileId || !state.uploadId) {
    const r = await withRetry('L’ouverture de l’envoi', () => post('/api/loge/studio/file/start', {
      ...(input.cover ? { projectId: input.cover.projectId, cover: true } : { folderId: input.folderId }),
      name: file.name, mime: file.type || 'application/octet-stream', size: file.size,
      ...(input.caption ? { caption: input.caption } : {}), downloadable: !!input.downloadable, previewPages: previews.length,
      ...(input.xr ? { xr: input.xr } : {}),
    }), signal);
    state.fileId = str(r, 'fileId', 'id');
    state.uploadId = str(r, 'uploadId', 'upload_id');
    state.version = num(r, 1, 'version');
    const cb = num(r, CHUNK_BYTES, 'chunkBytes', 'chunk_bytes');
    state.chunkBytes = cb > 0 ? Math.min(cb, CHUNK_BYTES) : CHUNK_BYTES;
    state.parts = [];
    state.previewsSent = 0;
    state.modelSent = false;
    state.modelError = null;
    if (!state.fileId || !state.uploadId) throw new LogeError('Le serveur n’a pas ouvert l’envoi (réponse incomplète).', 'server', 0);
  }

  const ranges = chunkRanges(file.size, state.chunkBytes);
  const doneBytes = () => ranges.filter((r) => state.parts.some((p) => p.part === r.part)).reduce((s, r) => s + (r.end - r.start), 0);
  let previewDone = previews.slice(0, state.previewsSent).reduce((s, b) => s + b.size, 0);
  onProgress(doneBytes() + previewDone, total);

  for (const range of ranges) {
    if (state.parts.some((p) => p.part === range.part)) continue;
    const base = doneBytes() + previewDone;
    const url = `/api/loge/studio/file/chunk?file=${q(state.fileId)}&part=${range.part}&upload=${q(state.uploadId)}`;
    const r = await withRetry(`Le morceau ${range.part} sur ${ranges.length}`, () => putBinary(url, file.slice(range.start, range.end), 'application/octet-stream', (loaded) => onProgress(base + Math.min(loaded, range.end - range.start), total), signal), signal);
    const etag = str(r, 'etag');
    if (!etag) throw new LogeError(`Le serveur n’a pas confirmé le morceau ${range.part} (étiquette absente).`, 'server', 0);
    state.parts.push({ part: range.part, etag });
  }

  // Aperçus : PREVIEW_PARALLEL à la fois. `previewsSent` ne compte que les pages envoyées sans trou depuis la première :
  // un nouvel essai reprend là, quitte à renvoyer une page déjà partie (elle est simplement remplacée).
  if (state.previewsSent < previews.length) {
    const fileId = state.fileId;
    const chunksDone = doneBytes();
    const sentAhead = new Set<number>();
    const inFlight = new Map<number, number>();
    let next = state.previewsSent;
    let failed = false;
    const report = () => onProgress(chunksDone + previewDone + [...inFlight.values()].reduce((s, n) => s + n, 0), total);
    const worker = async (): Promise<void> => {
      while (!failed && next < previews.length) {
        const page = next++;
        const blob = previews[page]!;
        const url = `/api/loge/studio/file/preview?file=${q(fileId)}&page=${page}`;
        try {
          await withRetry(`L’aperçu ${page + 1} sur ${previews.length}`, () => putBinary(url, blob, 'image/jpeg', (loaded) => { inFlight.set(page, Math.min(loaded, blob.size)); report(); }, signal), signal);
        } catch (e) {
          failed = true;
          throw e;
        } finally {
          inFlight.delete(page);
        }
        previewDone += blob.size;
        sentAhead.add(page);
        while (sentAhead.delete(state.previewsSent)) state.previewsSent++;
        report();
      }
    };
    await Promise.all(Array.from({ length: Math.min(PREVIEW_PARALLEL, previews.length - next) }, worker));
  }

  // Maquette 3D : un échec ne bloque pas le fichier, qui part sans elle (« Maquette 3D non envoyée : … »).
  if (model && !state.modelSent && !state.modelError) {
    const base = doneBytes() + previewDone;
    const url = `/api/loge/studio/file/model?file=${q(state.fileId)}`;
    try {
      await withRetry('La maquette 3D', () => putBinary(url, model, 'model/gltf-binary', (loaded) => onProgress(base + Math.min(loaded, model.size), total), signal), signal);
      state.modelSent = true;
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') throw e;
      state.modelError = e instanceof Error ? e.message : String(e);
    }
  }

  await withRetry('La clôture de l’envoi', () => post('/api/loge/studio/file/finish', { fileId: state.fileId, uploadId: state.uploadId, parts: [...state.parts].sort((a, b) => a.part - b.part) }), signal);
  state.finished = true;
  onProgress(total, total);
  return state;
}
